import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';

const FeaturesSection = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  
  useEffect(() => {
    const fadeElems = document.querySelectorAll('.fade-in');
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1 });
    
    fadeElems.forEach(elem => observer.observe(elem));
    
    return () => {
      fadeElems.forEach(elem => observer.unobserve(elem));
    };
  }, []);
  
  const features = [
    {
      title: t('features.expertise'),
      description: language === 'fr' 
        ? 'Partenaire certifié Baker Hughes avec une expertise reconnue en instrumentation industrielle pétrochimique.' 
        : language === 'ar' 
        ? 'شريك معتمد من بيكر هيوز مع خبرة معترف بها في الأجهزة الصناعية البتروكيماوية.' 
        : 'Baker Hughes certified partner with recognized expertise in petrochemical industrial instrumentation.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '32px', height: '32px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: t('features.safety'),
      description: language === 'fr' 
                ? 'Notre équipe est entièrement certifiée, garantissant les normes de sécurité les plus élevées dans toutes nos opérations.'
        : language === 'ar' 
        ? 'فريقنا معتمد بالكامل، مما يضمن أعلى معايير السلامة في جميع عملياتنا.'
        : 'Our team is fully certified, ensuring the highest safety standards in all operations.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '32px', height: '32px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: t('features.support'),
      description: language === 'fr' 
        ? 'Service client et support technique disponibles 24h/24 et 7j/7 pour répondre rapidement à tous vos besoins.' 
        : language === 'ar' 
        ? 'خدمة العملاء والدعم الفني متاحان على مدار الساعة طوال أيام الأسبوع لتلبية جميع احتياجاتك على الفور.' 
        : 'Round-the-clock customer service and technical support to address all your needs promptly.',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '32px', height: '32px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
        </svg>
      )
    }
  ];
  
  return (
    <section className="section section-dark" style={{ 
        position: 'relative',
        overflow: 'hidden'
      }}>
      <div style={{
        position: 'absolute',
        top: '5%',
        right: '5%',
        width: '300px',
        height: '300px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 107, 0, 0.03) 0%, rgba(255, 107, 0, 0) 70%)',
        zIndex: 1
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: '5%',
        left: '5%',
        width: '250px',
        height: '250px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255, 107, 0, 0.03) 0%, rgba(255, 107, 0, 0) 70%)',
        zIndex: 1
      }}></div>
      
      <div className="container" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
        <div className="text-center mb-16" style={{ textAlign: 'center' }}>
          <span className="fade-in" style={{
            display: 'inline-block',
            padding: '0.5rem 1.5rem',
            backgroundColor: 'rgba(255, 107, 0, 0.1)',
            color: 'var(--assco-orange)',
            fontWeight: '600',
            fontSize: '0.875rem',
            borderRadius: 'var(--radius-full)',
            marginBottom: '1rem'
          }}>
            {language === 'fr' ? 'CE QUI NOUS DISTINGUE' : 
             language === 'ar' ? 'ما يميزنا' : 
             'WHAT SETS US APART'}
          </span>
          <h2 className="features-title fade-in" style={{
            marginBottom: '1.25rem',
            position: 'relative',
            paddingBottom: '1.25rem',
            display: 'block',
            color: 'white',
            textAlign: 'center',
            margin: '0 auto 1.25rem auto'
          }}>
            {language === 'fr' ? 'Nos Caractéristiques Clés' : 
             language === 'ar' ? 'ميزاتنا الرئيسية' : 
             'Our Key Features'}
            <span style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              width: '80px',
              height: '4px',
              backgroundColor: 'var(--assco-orange)',
              borderRadius: '2px'
            }}></span>
          </h2>
          <p className="subtitle fade-in" style={{ 
            maxWidth: '800px', 
            margin: '0 auto',
            fontSize: '1.125rem',
            color: 'rgba(255, 255, 255, 0.8)',
            lineHeight: '1.7'
          }}>
            {language === 'fr' 
              ? 'Nous offrons une valeur exceptionnelle grâce à notre expertise de pointe dans l\'industrie et notre engagement envers l\'excellence.' 
              : language === 'ar' 
              ? 'نحن نقدم قيمة استثنائية من خلال خبرتنا الرائدة في الصناعة والتزامنا بالتميز.' 
              : 'We provide exceptional value through our industry-leading expertise and commitment to excellence.'}
          </p>
        </div>
        
        <div className="grid" style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
          gap: '2.5rem' 
        }}>
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="feature-card-enhanced fade-in"
              style={{ 
                animationDelay: `${0.2 + index * 0.2}s`,
                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                borderRadius: 'var(--radius-xl)',
                padding: '2.75rem',
                boxShadow: 'var(--shadow-lg)',
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                border: '1px solid rgba(255, 255, 255, 0.15)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-10px)';
                e.currentTarget.style.boxShadow = '0 20px 30px rgba(0, 0, 0, 0.15)';
                e.currentTarget.style.borderColor = 'var(--assco-orange)';
                const iconBg = e.currentTarget.querySelector('.feature-icon-bg');
                if (iconBg) {
                  iconBg.style.backgroundColor = 'var(--assco-orange)';
                  iconBg.style.transform = 'rotate(0deg) scale(1.1)';
                  iconBg.style.boxShadow = '0 10px 20px rgba(255, 107, 0, 0.3)';
                }
                const icon = e.currentTarget.querySelector('.feature-icon-svg');
                if (icon) {
                  icon.style.color = 'white';
                  icon.style.transform = 'rotate(0deg) scale(1.1)';
                }
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                const iconBg = e.currentTarget.querySelector('.feature-icon-bg');
                if (iconBg) {
                  iconBg.style.backgroundColor = 'rgba(255, 107, 0, 0.1)';
                  iconBg.style.transform = 'rotate(10deg)';
                  iconBg.style.boxShadow = '0 5px 15px rgba(255, 107, 0, 0.15)';
                }
                const icon = e.currentTarget.querySelector('.feature-icon-svg');
                if (icon) {
                  icon.style.color = 'var(--assco-orange)';
                  icon.style.transform = 'rotate(-10deg)';
                }
              }}
            >
              <div 
                className="feature-icon-bg"
                style={{
                  width: '85px',
                  height: '85px',
                  borderRadius: '18px',
                  backgroundColor: 'rgba(255, 107, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1.75rem',
                  transition: 'all 0.3s ease',
                  transform: 'rotate(10deg)',
                  boxShadow: '0 5px 15px rgba(255, 107, 0, 0.15)'
                }}
              >
                <div className="feature-icon-svg" style={{ 
                  color: 'var(--assco-orange)', 
                  transition: 'all 0.3s ease',
                  transform: 'rotate(-10deg)'
                }}>
                  {feature.icon}
                </div>
              </div>
              
              <h3 style={{ 
                fontSize: '1.5rem', 
                marginBottom: '1rem', 
                color: 'white',
                position: 'relative',
                paddingBottom: '0.75rem',
              }}>
                {feature.title}
                <span style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '40px',
                  height: '3px',
                  backgroundColor: 'var(--assco-orange)',
                  borderRadius: '2px',
                  opacity: 0.7
                }}></span>
              </h3>
              
              <p style={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                lineHeight: '1.7',
                fontSize: '1.05rem',
                marginBottom: '1.5rem'
              }}>
                {feature.description}
              </p>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                color: 'var(--assco-orange)',
                fontWeight: '600',
                fontSize: '0.875rem',
                transition: 'all 0.3s ease',
                opacity: 0.7,
                cursor: 'pointer'
              }}
              onMouseOver={(e) => {
                e.target.style.opacity = '1';
                e.target.style.transform = 'translateX(5px)';
              }}
              onMouseOut={(e) => {
                e.target.style.opacity = '0.7';
                e.target.style.transform = 'translateX(0)';
              }}
              >
                {language === 'fr' ? 'En savoir plus' : 
                 language === 'ar' ? 'اكتشف المزيد' : 
                 'Learn more'}
                <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '16px', height: '16px', marginLeft: '5px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
              
              {/* Decorative element */}
              <div style={{
                position: 'absolute',
                top: '0',
                right: '0',
                width: '100px',
                height: '100px',
                background: 'radial-gradient(circle at top right, rgba(255, 107, 0, 0.1) 0%, rgba(255, 107, 0, 0) 70%)',
                borderTopRightRadius: 'var(--radius-lg)',
                zIndex: '-1'
              }}></div>
            </div>
          ))}
        </div>
      </div>
      
      <style jsx>{`
        .features-title::after {
          display: none !important;
        }
      `}</style>
    </section>
  );
};

export default FeaturesSection;
